/* PdfFilenameTemplatesAdminPage — Batch T follow-up (Feb 2026)
 *
 * Tiny operator-facing editor for the PDF filename templates that live
 * in `settings.pdf_filename_templates`. The backend infra (defaults,
 * token substitution, sanitisation, cache invalidation) ships with
 * `services.pdf_filename`; this page is just the UI surface so Bob can
 * tweak `A6903 Smith - Quote Package.pdf` → any pattern without a code
 * push.
 *
 * Row shape (from GET /settings/pdf-filename-templates):
 *   { kind, label, default, override }
 *
 * UX:
 *   - Each row: label + input + live preview + "Reset to default" chip.
 *   - Blank input = revert to shipped default on save.
 *   - Token cheat-sheet panel on the right; clicking a chip inserts the
 *     token at the caret of the currently-focused row's input.
 *   - Single "Save all" PUT so partial edits don't leave the settings
 *     doc half-updated.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RefreshCw, Save, RotateCcw, FileText, Copy, Info, Wand2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// The 3 primary Go-Live reports — pinned to the top of the list with
// an accent so operators know these are the ones Bob's hard-drive
// muscle memory expects to see first.
const PRIMARY_KINDS = new Set(['quote_package', 'quotation_detailed', 'terms_of_trade_simple']);

export default function PdfFilenameTemplatesAdminPage() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);      // [{ kind, label, default, override }]
  const [tokens, setTokens] = useState([]);  // [{ token, label, example }]
  const [meta, setMeta] = useState({ updated_at: '', updated_by: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previews, setPreviews] = useState({});   // kind → filename
  const [dirty, setDirty] = useState(false);
  const inputRefs = useRef({});                    // kind → HTMLInputElement
  const [focusedKind, setFocusedKind] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings/pdf-filename-templates');
      setRows(Array.isArray(data.report_kinds) ? data.report_kinds : []);
      setTokens(Array.isArray(data.tokens) ? data.tokens : []);
      setMeta({ updated_at: data.updated_at || '', updated_by: data.updated_by || '' });
      setDirty(false);
    } catch (e) {
      toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Debounced preview — re-render each row's filename on any edit.
  useEffect(() => {
    if (!rows.length) return;
    const t = setTimeout(async () => {
      const next = {};
      await Promise.all(rows.map(async (r) => {
        const tpl = (r.override || r.default || '').trim();
        if (!tpl) { next[r.kind] = ''; return; }
        try {
          const { data } = await api.post('/settings/pdf-filename-templates/preview', {
            report_kind: r.kind, template: tpl,
          });
          next[r.kind] = data?.filename || '';
        } catch (_) {
          next[r.kind] = '';
        }
      }));
      setPreviews(next);
    }, 250);
    return () => clearTimeout(t);
  }, [rows, api]);

  const sortedRows = useMemo(() => {
    // Primaries first (in the fixed order below), then everything else
    // in the order the backend returned them.
    const primaryOrder = ['quote_package', 'quotation_detailed', 'terms_of_trade_simple'];
    const primaries = primaryOrder
      .map(k => rows.find(r => r.kind === k))
      .filter(Boolean);
    const rest = rows.filter(r => !PRIMARY_KINDS.has(r.kind));
    return [...primaries, ...rest];
  }, [rows]);

  const setOverride = (kind, value) => {
    setRows(prev => prev.map(r => r.kind === kind ? { ...r, override: value } : r));
    setDirty(true);
  };

  const resetRow = (kind) => {
    setRows(prev => prev.map(r => r.kind === kind ? { ...r, override: '' } : r));
    setDirty(true);
  };

  const insertTokenAtCaret = (token) => {
    const kind = focusedKind || sortedRows[0]?.kind;
    if (!kind) return;
    const el = inputRefs.current[kind];
    const current = (rows.find(r => r.kind === kind)?.override) || '';
    // If the input is focused, splice at the caret. Otherwise append.
    if (el && document.activeElement === el) {
      const start = el.selectionStart ?? current.length;
      const end   = el.selectionEnd   ?? current.length;
      const next  = current.slice(0, start) + token + current.slice(end);
      setOverride(kind, next);
      // Restore caret after React re-renders.
      setTimeout(() => {
        try {
          el.focus();
          const pos = start + token.length;
          el.setSelectionRange(pos, pos);
        } catch (_) {}
      }, 0);
    } else {
      setOverride(kind, current + token);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send non-empty overrides. Backend treats empty as "revert
      // to default" so keeping this map tight avoids storing junk.
      const templates = {};
      rows.forEach(r => {
        const v = (r.override || '').trim();
        if (v) templates[r.kind] = v;
      });
      const { data } = await api.put('/settings/pdf-filename-templates', { templates });
      toast.success(`Saved · ${data.saved_count} override(s)`);
      setDirty(false);
      await load();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="pdf-filename-templates-loading" className="flex items-center justify-center h-64 text-slate-500">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading templates…
      </div>
    );
  }

  return (
    <div data-testid="pdf-filename-templates-admin-page" className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">PDF Filename Templates</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Control what downloaded PDFs are called on disk. Templates use tokens like
            <code className="mx-1 px-1 rounded bg-slate-100 text-[11px]">{'{a_no}'}</code>
            and <code className="mx-1 px-1 rounded bg-slate-100 text-[11px]">{'{client_last}'}</code>.
            Leave a row blank to fall back to the shipped default.
          </p>
          {meta.updated_at ? (
            <p className="text-[11px] text-slate-400 mt-1.5">Last saved {new Date(meta.updated_at).toLocaleString()} by {meta.updated_by || 'unknown'}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="reload-templates-btn"
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 hover:text-slate-900 border border-[#E2E2DF] rounded-md hover:bg-slate-50"
          ><RefreshCw size={13} /> Reload</button>
          <button
            data-testid="save-templates-btn"
            disabled={!dirty || saving}
            onClick={handleSave}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md transition-colors ${dirty && !saving ? 'bg-[#D35400] text-white hover:bg-[#B84A00]' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {saving ? 'Saving…' : 'Save all'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* ── Table of report kinds ─────────────────────────────── */}
        <div data-testid="templates-table" className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
          <div className="grid grid-cols-[minmax(160px,1.2fr)_minmax(220px,2fr)_minmax(200px,2fr)_auto] gap-3 px-4 py-2.5 bg-slate-50 border-b border-[#E2E2DF] text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
            <div>Report</div>
            <div>Template</div>
            <div>Preview (sample)</div>
            <div className="text-right pr-1">Reset</div>
          </div>
          {sortedRows.map((r) => {
            const isPrimary = PRIMARY_KINDS.has(r.kind);
            const effective = (r.override || r.default || '').trim();
            const usingDefault = !(r.override || '').trim();
            return (
              <div
                key={r.kind}
                data-testid={`template-row-${r.kind}`}
                className={`grid grid-cols-[minmax(160px,1.2fr)_minmax(220px,2fr)_minmax(200px,2fr)_auto] gap-3 items-center px-4 py-3 border-b border-[#F1EFEC] last:border-b-0 ${isPrimary ? 'bg-[#FFF7EC]' : ''}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <FileText size={13} className={isPrimary ? 'text-[#D35400]' : 'text-slate-400'} />
                    <div className="text-sm font-semibold text-slate-800 truncate">{r.label}</div>
                  </div>
                  <div className="text-[10.5px] text-slate-400 font-mono truncate mt-0.5">{r.kind}</div>
                  {isPrimary && (
                    <span className="inline-block mt-1 text-[9.5px] uppercase tracking-[0.15em] font-bold text-[#D35400]">Primary</span>
                  )}
                </div>
                <div className="min-w-0">
                  <input
                    ref={(el) => { inputRefs.current[r.kind] = el; }}
                    data-testid={`template-input-${r.kind}`}
                    value={r.override || ''}
                    placeholder={r.default}
                    onFocus={() => setFocusedKind(r.kind)}
                    onChange={(e) => setOverride(r.kind, e.target.value)}
                    className="w-full px-3 py-1.5 text-xs font-mono border border-[#E2E2DF] rounded focus:outline-none focus:ring-2 focus:ring-[#D35400]/30 focus:border-[#D35400]"
                  />
                  {usingDefault && (
                    <div className="text-[10px] text-slate-400 mt-1 truncate">Using default: <span className="font-mono">{r.default}</span></div>
                  )}
                </div>
                <div className="min-w-0">
                  <div
                    data-testid={`template-preview-${r.kind}`}
                    className="text-xs font-mono text-slate-700 truncate px-2 py-1.5 bg-slate-50 rounded border border-slate-100"
                    title={previews[r.kind] || ''}
                  >
                    {previews[r.kind] || <span className="text-slate-300">—</span>}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-1">
                  <button
                    data-testid={`copy-preview-${r.kind}`}
                    disabled={!previews[r.kind]}
                    onClick={() => {
                      if (!previews[r.kind]) return;
                      navigator.clipboard?.writeText(previews[r.kind]);
                      toast.success('Copied');
                    }}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Copy preview"
                  ><Copy size={13} /></button>
                  <button
                    data-testid={`reset-template-${r.kind}`}
                    disabled={usingDefault}
                    onClick={() => resetRow(r.kind)}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-[#D35400] disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Reset to default"
                  ><RotateCcw size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Token cheat-sheet ─────────────────────────────────── */}
        <aside data-testid="token-cheatsheet" className="bg-white border border-[#E2E2DF] rounded-md p-4 h-fit sticky top-4">
          <div className="flex items-center gap-2 mb-3">
            <Wand2 size={15} className="text-[#D35400]" />
            <div className="text-sm font-semibold text-slate-800">Tokens</div>
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed mb-3">
            Click to insert into the focused row. Sample values come from the Access DB example <span className="font-mono">A6903 Smith</span>.
          </p>
          <div className="flex flex-col gap-1.5">
            {tokens.map((t) => (
              <button
                key={t.token}
                data-testid={`insert-token-${t.token.replace(/[{}]/g, '')}`}
                onClick={() => insertTokenAtCaret(t.token)}
                className="text-left group flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border border-transparent hover:border-[#D35400]/40 hover:bg-[#FFF7EC] transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-[11.5px] font-mono text-[#D35400] truncate">{t.token}</div>
                  <div className="text-[10px] text-slate-500 truncate">{t.label}</div>
                </div>
                <div className="text-[10px] text-slate-400 font-mono truncate max-w-[100px] group-hover:text-slate-600">{t.example}</div>
              </button>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-[#F1EFEC] flex items-start gap-2 text-[10.5px] text-slate-500 leading-relaxed">
            <Info size={12} className="text-slate-400 flex-shrink-0 mt-0.5" />
            <div>
              Illegal filename characters (<span className="font-mono">/ : * ? " &lt; &gt; |</span>) are auto-replaced with dashes.
              <span className="font-mono">.pdf</span> is appended automatically.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
